import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Breakpoint = "mobile" | "tablet" | "desktop";

interface ResponsiveContextValue {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouch: boolean;
  /** Pure width-based breakpoint, matches Tailwind sm/md. */
  breakpoint: Breakpoint;
  width: number;
  height: number;
}

const ResponsiveContext = createContext<ResponsiveContextValue | undefined>(undefined);

// Width-based breakpoints (mirror Tailwind's sm/md so CSS media queries
// and JS checks stay aligned for code that reads `breakpoint` directly).
const BREAKPOINTS = {
  mobile: 640, // sm
  tablet: 768, // md
} as const;

// Below this min-dimension (in CSS px), a touch device is treated as a
// phone — even if the wider edge crosses our width thresholds. Phones in
// landscape have width ~750–930 but height ~390–430; iPads in landscape
// are ~820+ in both dimensions, so they don't trigger this.
const PHONE_MIN_DIM_PX = 480;

function getBreakpoint(width: number): Breakpoint {
  if (width < BREAKPOINTS.mobile) return "mobile";
  if (width < BREAKPOINTS.tablet) return "tablet";
  return "desktop";
}

function detectTouch(): boolean {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

export function ResponsiveProvider({ children }: { children: ReactNode }) {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    setIsTouch(detectTouch());
    const handleResize = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const breakpoint = getBreakpoint(dimensions.width);

  // `isMobile` is decoupled from `breakpoint` because a phone in landscape
  // (e.g. 844×390) crosses the width thresholds even though the device is
  // still a phone. Code that needs strict viewport-width semantics (e.g.
  // matching Tailwind `sm:` rules) should read `breakpoint` directly.
  const minDim = Math.min(dimensions.width, dimensions.height);
  const isMobile = breakpoint === "mobile" || (isTouch && minDim < PHONE_MIN_DIM_PX);
  const isTablet = !isMobile && breakpoint === "tablet";
  const isDesktop = !isMobile && breakpoint === "desktop";

  return (
    <ResponsiveContext.Provider
      value={{
        isMobile,
        isTablet,
        isDesktop,
        isTouch,
        breakpoint,
        width: dimensions.width,
        height: dimensions.height,
      }}
    >
      {children}
    </ResponsiveContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useResponsive(): ResponsiveContextValue {
  const context = useContext(ResponsiveContext);
  if (context === undefined) {
    throw new Error("useResponsive must be used within a ResponsiveProvider");
  }
  return context;
}
